import { useCallback } from "react";

import { useTranslator } from "@eo-locale/react";

import Cookies from "js-cookie";

import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import {
  likeMarker,
  undoLikeMarker
} from "@/redux/features/place-marker/thunk";
import { useToast } from "@/storybook-components";
import {
  setCurrentPageNumber,
  setMarker,
  setPreviousMarker
} from "@/redux/features/place-marker/slice";
import { setPinDetails } from "@/redux/features/place/slice";
import { fetchPinDetails } from "@/redux/features/place-markers/thunk";
import useAccessStatus from "./use-access-status";

const SORT_ORDER = {
  newest: "created_at",
  topPins: "votes_count"
};

const useVotes = () => {
  const pins = useAppSelector((state) => state.placeMarkersSlice.markers);
  const dispatch = useAppDispatch();
  const projectId = useAppSelector((state) => state.project.project.id);
  const placesId = useAppSelector((state) => state.place.place.id);
  const { accessStatus, toastTitle } = useAccessStatus();
  const { translate } = useTranslator();
  const filteredCategoriesData = useAppSelector(
    (state) => state.placeMarkersSlice.filteredCategoriesData
  );
  const { categories, isSelfPin, sortOrder } = filteredCategoriesData;
  const rightPanelView = useAppSelector(
    (state) => state.placesRightPanelView.view
  );
  const pinDetails = useAppSelector((state) => state.place.pinDetails);
  const { prevMarkers } = useAppSelector((state) => state.placesMarkerSlice);
  const toast = useToast({
    isClosable: true,
    containerStyle: {
      marginBottom: "1 !important"
    }
  });

  const { id: userId } = useAppSelector(
    (state) => state.user.registeredUser.profile
  );

  const fetchPinDetailsWithSelectedData = useCallback(async () => {
    if (!prevMarkers.length) {
      dispatch(setCurrentPageNumber(1));
      await dispatch(
        fetchPinDetails({
          projectId,
          placesId,
          filters: {
            ...(isSelfPin && userId && { user_id: userId }),
            sort: { [SORT_ORDER[sortOrder]]: "desc" },
            category_id: categories
          }
        })
      );
    }
  }, [filteredCategoriesData, projectId, placesId, userId, prevMarkers]);

  const handleError = () => {
    if (!toast.isActive("marker-like-toast")) {
      toast({
        id: "marker-like-toast",
        title: translate("errors.sorrySomethingWentWrong"),
        status: "error"
      });
    }
  };

  const triggerVoteApi = async (id) => {
    const res: any = await dispatch(
      likeMarker({ projectId, placesId, markerId: id })
    );

    const responseStatus = res.payload.status;

    if (responseStatus) {
      handleError();
      return;
    }

    if (!toast.isActive("like-pin-toast")) {
      toast({
        title: translate("brainstormers.addVotesDesc"),
        id: "like-pin-toast",
        status: "success"
      });
      Cookies.remove("trigger_point");
    }
  };

  const handleVote = async (id, pin) => {
    try {
      await triggerVoteApi(id);

      // If vote api is called after reloading the page
      if (!pin) return;

      const updatedMarker = {
        ...pin,
        voted: true,
        totalVotes: pin.totalVotes + 1
      };

      dispatch(setMarker({ updatedMarker }));

      if (prevMarkers?.length) {
        const updatedMarkerState = prevMarkers.map((pinsData) =>
          pinsData.id === id
            ? {
                ...pinsData,
                totalVotes: pinsData.totalVotes + 1,
                voted: !pinsData.voted ? true : pinsData.voted
              }
            : pinsData
        );
        dispatch(setPreviousMarker(updatedMarkerState));
      }

      fetchPinDetailsWithSelectedData();

      if (["view-pin-details", "custom-marker"].includes(rightPanelView)) {
        dispatch(setPinDetails({ ...pinDetails, ...updatedMarker }));
      }
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      handleError();
    }
  };

  const triggerUnVoteApi = async (id) => {
    const res: any = await dispatch(
      undoLikeMarker({ projectId, placesId, markerId: id })
    );

    const responseStatus = res.payload.status;

    if (responseStatus) {
      handleError();
      return;
    }

    if (!toast.isActive("unvote-toast")) {
      toast({
        title: translate("brainstormers.removeVoteDesc"),
        id: "unvote-toast",
        status: "warning"
      });
      Cookies.remove("trigger_point");
    }
  };

  const handleUnVote = async (id, pin) => {
    try {
      await triggerUnVoteApi(id);

      // If un vote api is called after reloading the page
      if (!pin) return;

      const updatedMarker = {
        ...pin,
        voted: false,
        totalVotes: pin.totalVotes - 1
      };

      dispatch(setMarker({ updatedMarker }));
      if (prevMarkers?.length) {
        const updatedMarkerState = prevMarkers.map((pinsData) =>
          pinsData.id === id
            ? {
                ...pinsData,
                totalVotes: pinsData.totalVotes - 1,
                voted: pinsData.voted ? false : pinsData.voted
              }
            : pinsData
        );
        dispatch(setPreviousMarker(updatedMarkerState));
      }
      fetchPinDetailsWithSelectedData();

      if (["view-pin-details", "custom-marker"].includes(rightPanelView)) {
        dispatch(setPinDetails({ ...pinDetails, ...updatedMarker }));
      }
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      handleError();
    }
  };

  const findCurrentLikedPin = useCallback(
    (id, rightPanelView1) => {
      dispatch(setMarker({}));
      const filteredPin = pins?.filter((pin) => pin.id === id);
      if (!["view-pin-details", "custom-marker"].includes(rightPanelView1)) {
        return filteredPin[0];
      } else if (filteredPin.length) {
        return pinDetails;
      }
    },
    [pins, pinDetails, rightPanelView, prevMarkers]
  );

  const handleVoteToast = (id: string, toastTitle: string): void => {
    if (!toast.isActive(id)) {
      toast({
        title: translate(`brainstormers.${toastTitle}`),
        id: id,
        status: "warning"
      });
    }
  };

  const manageVotes = (id, rightPanelView) => {
    const filteredPin = findCurrentLikedPin(id, rightPanelView);

    if (accessStatus) {
      if (filteredPin?.voted) {
        handleUnVote(id, filteredPin);
      } else {
        handleVote(id, filteredPin);
      }
    } else {
      handleVoteToast("restrict-vote", toastTitle);
    }
  };

  return {
    manageVotes,
    handleUnVote,
    handleVote,
    fetchPinDetailsWithSelectedData,
    findCurrentLikedPin,
    triggerUnVoteApi,
    triggerVoteApi
  };
};

export default useVotes;
